// RealityAI Agent UI — AgentsPage (PHASE K7).
//
// Replaces the F8 «Агенты (скоро)» modal with the real Agent surface:
// list → create → select → configure → run → observe → stop → history.
//
// RAIOS Ch.11.7 (binding): fixed header, fixed navigation, fixed footer,
// scroll ONLY the agent list. With 50+ agents only the list column scrolls;
// the header, the detail pane and the footer never move (no page scroll).
//
// Feature flag (OQ-2): agents_enabled=false → the K6 API answers 404 on
// every route. The UI must stay in the ComingSoon/disabled state (a 404 is
// NOT a 500, and no partial functionality is exposed).

import { useCallback, useEffect, useMemo, useState } from "react";
import { Bot, Plus, X, RefreshCw, Search, AlertTriangle } from "lucide-react";
import {
  fetchAgents,
  createAgent,
  deleteAgent,
  Agent,
  isAgentsDisabledError,
  agentErrorInfo,
} from "@/lib/api/agents";
import { cn } from "@/lib/utils";
import { AgentDetail } from "./AgentDetail";
import { AgentRunView } from "./AgentRunView";
import type { AgentRun } from "@/lib/api/agents";

interface AgentsPageProps {
  onClose: () => void;
  // The entitlement-filtered model catalog (user.available_models.llm) —
  // reused for the run model override (no second catalog mechanism).
  modelNames: string[];
  modelDisplayNames: Record<string, string>;
  // Projects (for the project-bound agent marker) — existing state from
  // ChatPage (single source of truth; K7 adds no second permission layer).
  projectNameById?: (id: string) => string | null;
}

type ListState =
  | { kind: "loading" }
  | { kind: "disabled" } // 404 → agents_enabled=false (K6-4)
  | { kind: "error"; message: string }
  | { kind: "ready"; agents: Agent[] };

export function AgentsPage({
  onClose,
  modelNames,
  modelDisplayNames,
  projectNameById,
}: AgentsPageProps) {
  const [list, setList] = useState<ListState>({ kind: "loading" });
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // Run being observed: opened from the detail (start / history).
  const [activeRun, setActiveRun] = useState<{ agent: Agent; run: AgentRun } | null>(null);

  const load = useCallback(async () => {
    setList({ kind: "loading" });
    try {
      const agents = await fetchAgents();
      setList({ kind: "ready", agents });
    } catch (e) {
      if (isAgentsDisabledError(e)) {
        // K6-4: agents_enabled=false → graceful disabled state (not an error).
        setList({ kind: "disabled" });
      } else {
        const info = agentErrorInfo(e);
        setList({ kind: "error", message: info.detail || "Не удалось загрузить агентов" });
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (list.kind !== "ready") return [];
    const q = search.trim().toLowerCase();
    if (!q) return list.agents;
    return list.agents.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.description || "").toLowerCase().includes(q)
    );
  }, [list, search]);

  const selected = useMemo(
    () => (list.kind === "ready" ? list.agents.find((a) => a.id === selectedId) ?? null : null),
    [list, selectedId]
  );

  const handleCreated = useCallback((agent: Agent) => {
    setList((prev) =>
      prev.kind === "ready" ? { kind: "ready", agents: [agent, ...prev.agents] } : prev
    );
    setCreateOpen(false);
    setSelectedId(agent.id);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      setDeleteId(null);
      try {
        await deleteAgent(id);
        setList((prev) =>
          prev.kind === "ready" ? { kind: "ready", agents: prev.agents.filter((a) => a.id !== id) } : prev
        );
        if (selectedId === id) setSelectedId(null);
      } catch {
        /* keep the list as-is; the user can retry */
      }
    },
    [selectedId]
  );

  // ESC closes the run view first, then the page (never the whole app).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (activeRun) setActiveRun(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [activeRun, onClose]);

  // ---------- Disabled (agents_enabled=false) ----------
  if (list.kind === "disabled") {
    return (
      <div className="absolute inset-0 z-40 bg-app flex items-center justify-center">
        <div className="max-w-sm text-center px-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-hoverbg flex items-center justify-center mb-5">
            <Bot size={26} className="text-accent" strokeWidth={1.8} />
          </div>
          <h2 className="text-lg font-medium text-primary">Агенты скоро появятся</h2>
          <p className="mt-2 text-sm text-secondary leading-relaxed">
            Мультишаговые агенты готовятся. Функция будет доступна в следующем
            обновлении.
          </p>
          <button
            onClick={onClose}
            className="mt-6 h-9 px-5 rounded-lg bg-hoverbg hover:bg-hoverbg2 text-sm text-primary transition-colors"
          >
            Понятно
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-40 bg-app flex flex-col min-w-0">
      {/* ===== Header — FIXED (Ch.11.7): never scrolls ===== */}
      <div className="shrink-0 flex items-center gap-2 px-4 md:px-6 h-14 border-b border-hairline">
        <button
          onClick={onClose}
          className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
          aria-label="Закрыть"
        >
          <X size={18} />
        </button>
        <Bot size={18} className="text-accent shrink-0" strokeWidth={1.8} />
        <h1 className="text-sm font-medium text-primary">Агенты</h1>
        <div className="flex-1" />
        <button
          onClick={() => void load()}
          className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
          aria-label="Обновить"
        >
          <RefreshCw size={15} />
        </button>
        <button
          onClick={() => setCreateOpen(true)}
          disabled={list.kind !== "ready"}
          className="h-9 px-4 rounded-lg bg-accent/15 text-accent hover:bg-accent/25 text-sm font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50"
        >
          <Plus size={15} /> Создать агента
        </button>
      </div>

      {/* ===== Body: agent list (the ONLY scroll zone) + detail ===== */}
      <div className="flex-1 min-h-0 flex">
        {/* Agent list column — fixed width, own scroll (Ch.11.7) */}
        <div className="w-72 md:w-80 shrink-0 border-r border-hairline flex flex-col min-h-0">
          <div className="px-3 pt-3 pb-2 shrink-0">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary pointer-events-none"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по агентам…"
                className="w-full h-9 pl-8 pr-3 rounded-lg bg-surface border border-hairline text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent"
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-2">
            {list.kind === "loading" && (
              <div className="px-3 py-6 text-sm text-tertiary text-center">Загрузка…</div>
            )}
            {list.kind === "error" && (
              <div className="px-3 py-6 text-sm text-secondary text-center space-y-2">
                <AlertTriangle size={18} className="mx-auto text-red-400" />
                <div>{list.message}</div>
                <button onClick={() => void load()} className="text-accent hover:underline">
                  Повторить
                </button>
              </div>
            )}
            {list.kind === "ready" && filtered.length === 0 && (
              <div className="px-3 py-6 text-sm text-tertiary text-center">
                {search ? "Ничего не найдено" : "Агентов пока нет"}
              </div>
            )}
            {list.kind === "ready" &&
              filtered.map((agent) => (
                <AgentRow
                  key={agent.id}
                  agent={agent}
                  active={agent.id === selectedId}
                  projectName={agent.project_id ? (projectNameById?.(agent.project_id) ?? "Проект") : null}
                  onClick={() => setSelectedId(agent.id)}
                  onDelete={() => setDeleteId(agent.id)}
                />
              ))}
          </div>
        </div>

        {/* Detail pane — fixed (does not scroll with the list) */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          {selected ? (
            <AgentDetail
              key={selected.id}
              agent={selected}
              modelNames={modelNames}
              modelDisplayNames={modelDisplayNames}
              onChanged={(updated) =>
                setList((prev) =>
                  prev.kind === "ready"
                    ? { kind: "ready", agents: prev.agents.map((a) => (a.id === updated.id ? updated : a)) }
                    : prev
                )
              }
              onOpenRun={(run) => setActiveRun({ agent: selected, run })}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-tertiary">
              <Bot size={28} strokeWidth={1.8} className="mb-3 opacity-60" />
              <div className="text-sm">Выберите агента слева</div>
            </div>
          )}
        </div>
      </div>

      {/* ===== Footer — FIXED (Ch.11.7): never scrolls ===== */}
      <div className="shrink-0 h-9 border-t border-hairline flex items-center px-4 text-[11px] text-tertiary">
        {list.kind === "ready" && (
          <>Агентов: {list.agents.length}</>
        )}
      </div>

      {/* Create dialog */}
      {createOpen && (
        <AgentCreateDialog
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}

      {/* Delete confirmation (in-palette, never the native dialog) */}
      {deleteId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteId(null)} aria-hidden />
          <div className="relative w-full max-w-sm rounded-xl border border-strongline bg-elevated p-5 shadow-2xl">
            <h3 className="text-base font-medium text-primary">Удалить агента?</h3>
            <p className="mt-2 text-sm text-secondary">
              Агент будет удалён без возможности восстановления.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setDeleteId(null)}
                className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => void handleDelete(deleteId)}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500/90 text-white hover:bg-red-500 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Run view (observe / stop) — overlays the detail */}
      {activeRun && (
        <AgentRunView
          agent={activeRun.agent}
          initialRun={activeRun.run}
          onClose={() => {
            setActiveRun(null);
            void load(); // refresh history after the run settles
          }}
        />
      )}
    </div>
  );
}

// ---------- List row ----------

function AgentRow({
  agent,
  active,
  projectName,
  onClick,
  onDelete,
}: {
  agent: Agent;
  active: boolean;
  projectName: string | null;
  onClick: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        "group flex items-center gap-2 pl-3 pr-2 py-2 mx-1 rounded-lg cursor-pointer transition-colors mb-0.5",
        active ? "bg-hoverbg2" : "hover:bg-hoverbg"
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-primary truncate">{agent.name}</span>
          {projectName && (
            <span className="shrink-0 text-[9px] uppercase tracking-wide px-1 rounded border border-accent/40 text-accent">
              {projectName}
            </span>
          )}
          {agent.status === "archived" && (
            <span className="shrink-0 text-[9px] uppercase tracking-wide px-1 rounded border border-hairline text-tertiary">
              архив
            </span>
          )}
        </div>
        {agent.description && (
          <div className="text-xs text-tertiary truncate">{agent.description}</div>
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="md:hidden md:group-hover:flex p-1 rounded text-tertiary hover:text-red-400 transition-colors shrink-0"
        aria-label="Удалить"
      >
        <X size={13} />
      </button>
    </div>
  );
}

// ---------- Create dialog ----------

function AgentCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (a: Agent) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Укажите название агента");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const agent = await createAgent({
        name: trimmed,
        description: description.trim() || null,
        instructions: instructions.trim(),
      });
      onCreated(agent);
    } catch (e) {
      const info = agentErrorInfo(e);
      if (info.status === 403) setError("Недостаточно прав для создания агента");
      else if (info.status === 422) setError(info.detail || "Проверьте поля формы");
      else setError(info.detail || "Не удалось создать агента");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-xl border border-strongline bg-elevated p-5 shadow-2xl max-h-[85vh] overflow-y-auto">
        <h3 className="text-base font-medium text-primary">Новый агент</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs text-secondary mb-1">Название *</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="Например: Исследователь"
              className="w-full h-9 px-3 rounded-lg bg-surface border border-hairline text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-xs text-secondary mb-1">Описание</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              placeholder="Короткое описание (необязательно)"
              className="w-full h-9 px-3 rounded-lg bg-surface border border-hairline text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-xs text-secondary mb-1">Инструкции</label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={4}
              maxLength={20000}
              placeholder="Системные инструкции для агента (необязательно)"
              className="w-full px-3 py-2 rounded-lg bg-surface border border-hairline text-sm text-primary placeholder:text-tertiary focus:outline-none focus:border-accent resize-y"
            />
          </div>
          {error && <div className="text-sm text-red-400">{error}</div>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
          >
            Отмена
          </button>
          <button
            onClick={() => void submit()}
            disabled={busy}
            className="h-9 px-4 rounded-lg text-sm font-medium bg-accent/15 text-accent hover:bg-accent/25 transition-colors disabled:opacity-50"
          >
            {busy ? "Создание…" : "Создать"}
          </button>
        </div>
      </div>
    </div>
  );
}
